import { z } from 'zod'

import {
  isMeetingSplitDimension,
  type MeetingSplitDimension,
  meetingSplitDimensions,
  readTreeSplits,
} from './exploration'

import type { AttributionFilters } from '@/api/attributionObservability'

export const MAX_SHARED_SPLITS = 3
export const MAX_TREND_GROUPS = 12
const schema = z
  .array(z.enum(meetingSplitDimensions))
  .max(MAX_SHARED_SPLITS)
  .refine((items) => new Set(items).size === items.length)

export const availableSharedSplits = (filters: AttributionFilters, prior: MeetingSplitDimension[] = []) =>
  meetingSplitDimensions.filter(
    (dimension) => !prior.includes(dimension) && (!filters[dimension] || filters.negate?.includes(dimension))
  )

export const readSharedSplits = (
  search: URLSearchParams,
  filters: AttributionFilters
): { axes: MeetingSplitDimension[]; notice: string | null } => {
  const raw = search.getAll('split')
  if (search.has('split')) {
    if (raw.length === 1 && raw[0] === 'none') return { axes: [], notice: null }
    const parsed = schema.safeParse(raw)
    if (
      parsed.success &&
      parsed.data.every((axis, depth) => availableSharedSplits(filters, parsed.data.slice(0, depth)).includes(axis))
    )
      return { axes: parsed.data, notice: null }
    return {
      axes: [],
      notice: 'This saved view has unsupported shared splits.',
    }
  }
  if (!search.has('tree')) return { axes: [], notice: null }
  const tree = readTreeSplits(search, filters)
  const axes: MeetingSplitDimension[] = []
  for (let depth = 0; depth < MAX_SHARED_SPLITS; depth++) {
    const atDepth = Object.entries(tree)
      .filter(([key]) => (JSON.parse(key) as unknown[]).length === depth)
      .map(([, dimension]) => dimension)
    const dimensions = Array.from(new Set(atDepth.filter((axis) => axis !== null)))
    if (!dimensions.length) break
    if (dimensions.some((dimension) => !isMeetingSplitDimension(dimension)))
      return {
        axes: [],
        notice: 'Source and rule splits are now in the Speaker label sources section. Reset this view to continue.',
      }
    if (dimensions.length !== 1 || atDepth.includes(null))
      return {
        axes: [],
        notice: 'This saved view uses independent branches. Choose shared splits to compare them over time.',
      }
    if (isMeetingSplitDimension(dimensions[0])) axes.push(dimensions[0])
  }
  if (Object.keys(tree).some((key) => (JSON.parse(key) as unknown[]).length >= MAX_SHARED_SPLITS))
    return { axes: [], notice: 'Choose up to three shared split levels.' }
  return { axes, notice: null }
}

export const writeSharedSplits = (params: URLSearchParams, axes: MeetingSplitDimension[]) => {
  params.delete('tree')
  params.delete('split')
  for (const axis of axes.length ? axes : ['none']) params.append('split', axis)
}
